import { useCallback, useState } from 'react';

import toast from 'components/partials/toast/toast';

import {
  DEFAULT_SPECIAL_AWARD_AMOUNT,
  SPECIAL_AWARD_ADDED_TOAST,
  SPECIAL_AWARD_SAVE_ERROR_TOAST,
  SPECIAL_AWARD_UPDATED_TOAST,
} from '../calculations-step.constants';
import type { SpecialAwardRuleRequest } from './use-special-award-form';

import {
  useAddCalculationRules,
  useCreateCalculation,
  useUpdateCalculationRule,
} from '../../../hooks/use-aid-calculations';

import type { SpecialAwardRow } from '../calculations-step.types';
import type {
  AidModelCalculationDto,
  AidModelCalculationRequest,
  CalculationRuleRequest,
} from 'api/generated/schemas';

export interface UseSubmitSpecialAwardParams {
  userId: string;
  modelId: string | number;
  /** The model's calculation, when it already exists; created on the fly if not. */
  calculation?: AidModelCalculationDto;
  settingsToRequest: () => AidModelCalculationRequest;
  /** The rule being edited, or `undefined` in the add flow. */
  rule?: SpecialAwardRow;
  defaultAwardLogic: CalculationRuleRequest['awardLogic'];
  isValid: boolean;
  toRequests: () => SpecialAwardRuleRequest[];
  onClose: () => void;
  onSuccess?: () => void;
}

export interface UseSubmitSpecialAwardResult {
  submit: () => Promise<void>;
  /** Drives both the disabled state of the submit button and the guard in `submit`. */
  canSubmit: boolean;
  isSubmitting: boolean;
}

/**
 * Persists the modal's population-only requests as calculation rules.
 *
 * The calculation is a singleton per aid model and may not exist yet when the
 * first special award is added, so it is created from the settings draft first.
 * `awardAmount` / `awardLogic` are `@NotNull` on the backend but have no input
 * in this modal (ADO #36245): a new rule takes the defaults, an edited rule
 * keeps its own values. `toRequests` always returns at most one request —
 * every checked population plus every complete criteria block collapses into
 * a single row — but this still PUTs/POSTs generically over whatever
 * `toRequests` returns.
 */
export const useSubmitSpecialAward = ({
  userId,
  modelId,
  calculation,
  settingsToRequest,
  rule,
  defaultAwardLogic,
  isValid,
  toRequests,
  onClose,
  onSuccess,
}: UseSubmitSpecialAwardParams): UseSubmitSpecialAwardResult => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createCalculation = useCreateCalculation();
  const addRules = useAddCalculationRules();
  const updateRule = useUpdateCalculationRule();

  const isEditing = rule?.id != null;
  const canSubmit = isValid && !!defaultAwardLogic && !isSubmitting;

  const submit = useCallback(async (): Promise<void> => {
    if (!canSubmit || !userId || !modelId) {
      return;
    }

    const requests: CalculationRuleRequest[] = toRequests().map((request) => ({
      ...request,
      awardAmount: rule?.awardAmount ?? DEFAULT_SPECIAL_AWARD_AMOUNT,
      awardLogic: rule?.awardLogic ?? defaultAwardLogic,
    }));

    if (!requests.length) {
      return;
    }

    setIsSubmitting(true);

    try {
      let resolvedId = calculation?.id;

      if (resolvedId == null) {
        const created = await createCalculation.mutateAsync({
          userId,
          modelId,
          data: settingsToRequest(),
        });

        resolvedId = created?.id;
      }

      if (resolvedId == null) {
        throw new Error('Calculation could not be created');
      }

      const calculationId = resolvedId;

      if (isEditing && rule?.id != null) {
        await updateRule.mutateAsync({
          userId,
          modelId,
          calculationId,
          ruleId: rule.id,
          data: requests[0],
        });

        if (requests.length > 1) {
          await addRules.mutateAsync({
            userId,
            modelId,
            calculationId,
            requests: requests.slice(1),
          });
        }
      } else {
        await addRules.mutateAsync({
          userId,
          modelId,
          calculationId,
          requests,
        });
      }

      toast.success(
        isEditing ? SPECIAL_AWARD_UPDATED_TOAST : SPECIAL_AWARD_ADDED_TOAST
      );
      onSuccess?.();
      onClose();
    } catch (error) {
      const err = error as { data?: { detail?: string } };

      toast.error({
        title: SPECIAL_AWARD_SAVE_ERROR_TOAST.title,
        message: err?.data?.detail || SPECIAL_AWARD_SAVE_ERROR_TOAST.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    userId,
    modelId,
    toRequests,
    rule,
    defaultAwardLogic,
    calculation,
    settingsToRequest,
    createCalculation,
    addRules,
    updateRule,
    isEditing,
    onClose,
    onSuccess,
  ]);

  return { submit, canSubmit, isSubmitting };
};
